import { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import GradientCircularProgress from '../components/common/GradientCircularProgress';
import useBackendApi from '../hooks/useBackendApi';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

import EmptyVitalsState from './EmptyVitalsState';
import AddVitalsForm from './AddVitalsForm';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

interface VitalsTabProps {
  orgPathName: string;
  accPathName: string;
  patientUserId: string;
}

interface VitalRecord {
  observation_id: string;
  code: string;
  label?: string;
  value_number: number;
  value_unit: string;
  observation_date: string;
  encounter_id: string;
}

export default function VitalsTab({ orgPathName, accPathName, patientUserId }: VitalsTabProps) {
  const { theme } = useThemeContext();
  const { apiFetchRequest, isLoading } = useBackendApi();

  const [vitals, setVitals] = useState<VitalRecord[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isStaff, setIsStaff] = useState(false);

  const fetchVitals = useCallback(async () => {
    if (!orgPathName || !accPathName || !patientUserId) return;
    setErrorText(null);

    try {
      const endpoint = `${backendURL}/o/${encodeURIComponent(orgPathName)}/a/${encodeURIComponent(
        accPathName
      )}/patients/${encodeURIComponent(patientUserId)}/vitals`;

      const res = await apiFetchRequest({ endpoint, method: 'GET' });

      if (res?.status === 200) {
        setVitals(res.body.vitals || []);
        setIsStaff(!!res.body.is_staff);
      } else {
        setErrorText('Failed to load vitals.');
      }
    } catch {
      setErrorText('Failed to load vitals.');
    }
  }, [accPathName, apiFetchRequest, orgPathName, patientUserId]);

  useEffect(() => {
    fetchVitals();
  }, [fetchVitals]);

  const handleSuccess = () => {
    setIsAdding(false);
    fetchVitals();
  };

  if (isLoading && !vitals.length && !errorText && !isAdding) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <GradientCircularProgress />
      </Box>
    );
  }

  if (isAdding) {
    return (
      <AddVitalsForm
        orgPathName={orgPathName}
        accPathName={accPathName}
        patientUserId={patientUserId}
        onCancel={() => setIsAdding(false)}
        onSuccess={handleSuccess}
      />
    );
  }

  if (errorText) {
    return <Typography color="error">{errorText}</Typography>;
  }

  if (vitals.length === 0) {
    return <EmptyVitalsState onAddClick={isStaff ? () => setIsAdding(true) : undefined} />;
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.custom.pages.frost.authHome.cards.backgroundColor,
        borderRadius: '16px',
        padding: { xs: '20px', sm: '28px' },
        border: '1px solid',
        borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.custom.pages.frost.authHome.typography.body1.color,
          }}
        >
          Vitals History
        </Typography>
        {isStaff && (
          <Button variant="contained" onClick={() => setIsAdding(true)}>
            Add Vitals
          </Button>
        )}
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Vital</TableCell>
            <TableCell>Value</TableCell>
            <TableCell>Unit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vitals.map((v: VitalRecord) => (
            <TableRow key={v.observation_id}>
              <TableCell>
                {v.observation_date ? new Date(v.observation_date).toLocaleString() : '-'}
              </TableCell>
              <TableCell>{v.label || v.code}</TableCell>
              <TableCell>{v.value_number}</TableCell>
              <TableCell>{v.value_unit}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
